import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BankAccountForm } from 'src/components/BankAccountForm';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { createPaymentMethod } from 'src/models/payment-methods/payment-methods';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import { FranchiseFeePayer } from '../../../models/hq/franchise-fee-payer/franchise-fee-payer';

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  payer: FranchiseFeePayer | null;
  onPaymentMethodAdded: () => void;
}

const PaymentMethodDialog: React.FC<PaymentMethodDialogProps> = ({
  open,
  onClose,
  payer,
  onPaymentMethodAdded,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFeePayers',
  });
  const [isEditing, setIsEditing] = useState(false);

  const { mutate: createMethod } = useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: () => {
      toast.success(t('bankAccountAdded'));
      onPaymentMethodAdded();
      setIsEditing(false);
      onClose();
    },
    onError: (error: Error) => {
      toast.error(api.formatError(error, t('Error adding bank account')));
    },
  });

  const handlePaymentMethodAdded = (result: any) => {
    if (result.setupIntent?.status === 'succeeded') {
      // Create the payment method record in our database
      createMethod({
        token: result.setupIntent.id,
        userId: payer!.userId,
        paymentMethodType: result.setupIntent.payment_method?.type,
        status:
          result.setupIntent.status === 'requires_action'
            ? 'unverified'
            : 'verified',
        default: false,
      });
    } else {
      toast.error(t('Error adding bank account'));
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  if (!payer) return null;

  const hasPaymentMethod = payer.paymentMethod;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('paymentMethod')}
      maxWidth="sm"
      fullWidth
      actions={[{ label: t('cancel'), onClick: handleClose }]}
    >
      {hasPaymentMethod && !isEditing ? (
        <Stack spacing={3}>
          <Typography variant="body1">
            {t('Current bank account')}: {payer.paymentMethodMasked}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="bordered" onClick={() => setIsEditing(true)}>
              {t('Change bank account')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <BankAccountForm
          user={
            {
              id: payer.userId,
              email: payer.email,
              firstName: payer.firstName,
              lastName: payer.lastName,
            } as any
          }
          onPaymentMethodAdded={handlePaymentMethodAdded}
          onCancel={hasPaymentMethod ? () => setIsEditing(false) : handleClose}
          disabled={false}
          existingPaymentMethod={undefined}
        />
      )}
    </Dialog>
  );
};

export default PaymentMethodDialog;
